/**
 * ComposeMessageForm Component
 * Subject and message fields with validation and AI assist
 */

import { cn } from '@/lib/utils';
import { TYPOGRAPHY, SPACING } from '@/design-system';
import { RESPONSIVE } from '@/design-system/helpers/responsive';
import { useLocalizations } from '@/hooks/useLocalizations';
import { LComponents } from '@/components/i18n';
import { Label } from '@/components/ui/label';
import { ValidatedInput, ValidatedTextarea, FieldStatus } from '@/components/FormValidation';
import { ComposeAIAssist } from './ComposeAIAssist';
import type { ComposeFormData, Expert } from './types';

interface ComposeMessageFormProps {
  formData: ComposeFormData;
  errors: Record<string, string>;
  fieldStatus: Record<string, FieldStatus>;
  onFormChange: (updates: Partial<ComposeFormData>) => void;
  onFieldBlur: (field: string, value: string) => void;
  selectedExperts?: Expert[];
}

export function ComposeMessageForm({
  formData,
  errors,
  fieldStatus,
  onFormChange,
  onFieldBlur,
  selectedExperts = [],
}: ComposeMessageFormProps) {
  const { lComponents } = useLocalizations();

  return (
    <div className={SPACING.stackMd}>
      {/* Subject - Optional */}
      <div className={SPACING.stackSm}>
        <div className={RESPONSIVE.flexRowBetween}>
          <Label htmlFor="subject">
            <LComponents id="compose_message.labels.subject" defaultValue='Message Subject' />
          </Label>
          <p className={cn(
            TYPOGRAPHY.caption,
            formData.subject.length > 180 && "text-destructive"
          )}>
            {formData.subject.length} / 200
          </p>
        </div>
        <ValidatedInput
          id="subject"
          placeholder={lComponents('compose_message.placeholders.subject', { defaultValue: 'Subject' })}
          value={formData.subject}
          onChange={(e) => onFormChange({ subject: e.target.value })}
          onBlur={(e) => onFieldBlur('subject', e.target.value)}
          maxLength={200}
          error={errors.subject}
          fieldStatus={fieldStatus.subject}
        />
      </div>
      {/* Message - Required with AI Assist */}
      <div className={SPACING.stackSm}>
        <div className={RESPONSIVE.flexRowBetween}>
          <div className="flex items-center gap-2">
            <Label htmlFor="message">
              <LComponents id="compose_message.labels.describe_needs" defaultValue='Needs Description' />
            </Label>
            <ComposeAIAssist
              message={formData.message}
              formData={formData}
              selectedExperts={selectedExperts}
              onMessageChange={(message) => onFormChange({ message })}
              inline={true}
            />
          </div>
          <p className={cn(
            TYPOGRAPHY.caption,
            formData.message.length > 1800 
              ? "text-destructive" 
              : formData.message.length > 1600 
                ? "text-warning" 
                : ""
          )}>
            {formData.message.length} / 2000
          </p>
        </div>
        <ValidatedTextarea
          id="message"
          placeholder={lComponents('compose_message.placeholders.message', { defaultValue: 'Enter your message.' })}
          value={formData.message}
          onChange={(e) => onFormChange({ message: e.target.value })}
          onBlur={(e) => onFieldBlur('message', e.target.value)}
          rows={8}
          maxLength={2000}
          error={errors.message}
          fieldStatus={fieldStatus.message}
        />
      </div>
    </div>
  );
}
